import { useState, type KeyboardEvent } from 'react';
import { X, Plus } from 'lucide-react';
import { humanize } from '../lib/humanize';
import { Input, Button, Badge } from './ui';

interface TagPickerProps {
  values: string[];
  onChange: (next: string[]) => void;
  /** Suggested values that appear as quick-add chips below the input. */
  suggestions?: string[];
  /** Whether the picker is interactive. When false, just renders read-only chips. */
  disabled?: boolean;
  placeholder?: string;
}

/**
 * A simple multi-select picker: shows current values as removable chips,
 * with an input + add button to add new ones. Optional suggestions render
 * as click-to-add chips beneath.
 */
export function TagPicker({ values, onChange, suggestions = [], disabled = false, placeholder = 'Add a value' }: TagPickerProps) {
  const [draft, setDraft] = useState('');

  function addValue(raw: string) {
    const v = raw.trim().toLowerCase().replace(/\s+/g, '_');
    if (!v) return;
    if (values.includes(v)) return;
    onChange([...values, v]);
    setDraft('');
  }

  function removeValue(v: string) {
    onChange(values.filter((x) => x !== v));
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue(draft);
    }
  }

  const remainingSuggestions = suggestions.filter((s) => !values.includes(s));

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {values.length === 0 && <span className="text-sm text-soft">No values</span>}
        {values.map((v) => (
          <Badge key={v} variant="indigo" className="inline-flex items-center gap-1">
            <span>{humanize(v)}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeValue(v)}
                className="hover:text-rose-600 ml-0.5"
                aria-label={`Remove ${humanize(v)}`}
              >
                <X size={11} />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <>
          <div className="flex gap-2">
            <Input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              placeholder={placeholder}
              className="text-sm"
            />
            <Button type="button" size="sm" variant="secondary" onClick={() => addValue(draft)}>
              <Plus size={12} className="mr-1" />Add
            </Button>
          </div>
          {remainingSuggestions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-soft mb-1">Suggestions:</p>
              <div className="flex flex-wrap gap-1">
                {remainingSuggestions.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => addValue(s)}
                    className="text-xs px-2 py-0.5 rounded-full border border-line text-soft hover:bg-cream"
                  >
                    + {humanize(s)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
